"use client";

import { useEffect, useState } from "react";

/**
 * 2026-10-18 Reading Progress Bar for long-form editorial articles.
 * Zero layout shift, GPU-accelerated width transform, smooth rAF throttling.
 * Adheres to prefers-reduced-motion.
 */
export function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let ticking = false;

    function updateProgress() {
      const article = document.querySelector("article");
      if (!article) {
        ticking = false;
        return;
      }

      const rect = article.getBoundingClientRect();
      const articleTop = rect.top + window.scrollY;
      const articleHeight = rect.height;
      const windowHeight = window.innerHeight;
      const currentScroll = window.scrollY;

      // Calculate progress through the article body
      const start = articleTop;
      const end = articleTop + articleHeight - windowHeight;
      const total = end - start;

      if (total <= 0) {
        setProgress(100);
      } else {
        const scrolled = currentScroll - start;
        const pct = Math.min(100, Math.max(0, (scrolled / total) * 100));
        setProgress(pct);
      }

      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(updateProgress);
        ticking = true;
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    updateProgress();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <div
      className="fixed inset-x-0 top-0 z-[60] h-[3px] pointer-events-none bg-transparent"
      aria-hidden="true"
    >
      <div
        className="h-full origin-left bg-gradient-to-r from-[var(--hero-blue)] via-[var(--hero-magenta)] to-[var(--hero-orange)] shadow-[0_0_10px_color-mix(in_srgb,var(--hero-magenta)_60%,transparent)] transition-transform duration-100 ease-out motion-reduce:transition-none"
        style={{
          transform: `scaleX(${progress / 100})`,
        }}
      />
    </div>
  );
}
